import { message, Table, Tag } from "antd";
import Card from "../components/Card";
import { ColumnsType } from "antd/lib/table/interface";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { CheckOutlined, DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import * as Sentry from "@sentry/browser";
import {
  Product,
  QprReport,
  Company,
} from "../helpers/shared-interfaces";
import {
  request,
  getStateColor,
  formatSnakeCase,
  // getPageDetails,
} from "../helpers/utilities";
import { SEARCH_PRODUCTS } from "../gql/products";
import { GET_QPR_REPORTS } from "../gql/general";
import { SEARCH_COMPANY } from "../gql/companies";
import debounce from "lodash.debounce";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { PageToolbar } from "../components/PageToolbar";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isLoading: boolean;
  qprReports: QprReport[];
  qprReportsCount: number;
  page: number;
  pageSize: number;
  qprType: string;
  productId: string;
  quarter: string;
  year: string;
  companyId: string;
  products: Product[];
  companies: Company[];
  productSearchTerm: string;
  companySearchTerm: string;
}

type FilterKey = "qprType" | "productId" | "companyId" | "quarter" | "year";

const columns: ColumnsType<QprReport> = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (text: string, record: QprReport): JSX.Element => (
      <span>{record.name}</span>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (text: string, record: QprReport): JSX.Element => (
      <Tag
        color={getStateColor(record.status)}
        style={{ textTransform: "capitalize" }}
      >
        {formatSnakeCase(record.status)}
      </Tag>
    ),
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (text: string, record: QprReport): JSX.Element => (
      <span>
        {record.createdAt
          ? moment(record.createdAt, "YYYY-MM-DD HH:mm:ss").format(
            "DD/MM/YYYY HH:mm"
          )
          : "N/A"}
      </span>
    ),
  },
  {
    title: "Download",
    dataIndex: "url",
    key: "url",
    align: "center",
    render: (text: string, record: QprReport): JSX.Element => (
     record.url ? <a href={record.url} target="_blank" rel="noopener noreferrer">
        <DownloadOutlined />
      </a> : <></>
    ),
  },
  {
    title: "Notification Sent?",
    dataIndex: "notificationSent",
    key: "notificationSent",
    align: "center",
    render: (text: string, record: QprReport): JSX.Element =>
      record.notificationSent ? (
        <CheckOutlined style={{ color: "#52c41a", fontSize: 18 }} />
      ) : <></>,
  }
];

export default class QprReports extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      qprReports: [],
      qprReportsCount: 0,
      page: 1,
      pageSize: 25,
      qprType: "",
      productId: "",
      quarter: "",
      year: "",
      companyId: "",
      products: [],
      companies: [],
      productSearchTerm: "",
      companySearchTerm: "",
    };
  }

  getProducts(): void {
    request({
      query: SEARCH_PRODUCTS,
      variables: {
        page: 1,
        q: this.state.productSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        products: data.products.results,
        isLoading: false,
      });
    });
  }

  getCompanies(): void {
    request({
      query: SEARCH_COMPANY,
      variables: {
        page: 1,
        q: this.state.companySearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        companies: data.companySearch.results,
        isLoading: false,
      });
    });
  }

  getAllRecords = async (): Promise<void> => {
    const { qprType, productId, quarter, year, companyId, page, pageSize } = this.state;
    this.setState({ isLoading: true });

    const variables: any = {
      page,
      numberPerPage: pageSize,
    };
    if (qprType) variables.qprType = qprType;
    if (productId) variables.productId = productId;
    if (quarter) variables.quarter = quarter;
    if (year) variables.year = year;
    if (companyId) variables.companyId = companyId;

    try {
      const { data: { data } }: any = await request({
        query: GET_QPR_REPORTS,
        variables,
      });
      this.setState({
        qprReports: data.getQprReports.results,
        qprReportsCount: data.getQprReports.count,
      });
    } catch (error) {
      message.error(`Failed to fetch QPR exports - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleFilter = (key: FilterKey, value: string): void => {
    this.setState(
      {
        [key]: value,
        page: 1,
      } as any,
      () => {
        this.getAllRecords();
      }
    );
  };

  handlePageChange = async (page: number): Promise<void> => {
    this.setState({ page }, () => {
      this.getAllRecords();
    });
  };

  handleProductChange = async (searchTerm: string): Promise<void> => {
    this.setState({ productSearchTerm: searchTerm }, () => this.triggerDelayedSearchProduct());
  };

  handleCompanyChange = async (searchTerm: string): Promise<void> => {
    this.setState({ companySearchTerm: searchTerm }, () => this.triggerDelayedSearchCompany());
  };

  triggerDelayedSearchProduct = debounce(async (): Promise<void> => {
    return this.getProducts();
  }, DEBOUNCE_TIME);

  triggerDelayedSearchCompany = debounce(async (): Promise<void> => {
    return this.getCompanies();
  }, DEBOUNCE_TIME);

  async componentDidMount(): Promise<void> {
    this.getProducts();
    this.getCompanies();
    this.getAllRecords();
  }

  render(): JSX.Element {
    const { isLoading,
      qprReports,
      qprReportsCount,
      page,
      pageSize,
      products,
      companies,
      qprType,
      productId,
      companyId,
      quarter,
      year
    } = this.state;

    const quarterOptions = [
      { label: "Q1", value: "Q1" },
      { label: "Q2", value: "Q2" },
      { label: "Q3", value: "Q3" },
      { label: "Q4", value: "Q4" },
    ];

    let yearOptions: any[] = [];
    let currYear = new Date().getFullYear();
    for (let y: number = 2012; y <= currYear; y++) {
      yearOptions.push({
        label: y.toString(),
        value: y.toString(),
      });
    }

    const qprTypeOptions = [
      { value: "one_pager", label: "One Pager" },
      { value: "bulk", label: "Bulk" }
    ];

    const modifiedProducts = products.map((p) => ({ label: p.name, value: p.id }));
    const modifiedCompanies = companies.map((c) => ({ label: c.name, value: c.id }));

    return (
      <Card>
        <div className="qpr-exports-table-container">
          <PageToolbar modelName="reports"
            searchValue={this.state.productSearchTerm}
            onSearchChange={(e) => this.handleProductChange(e.target.value)}
            searchPlaceholder="Search reports by product..."
            totalCount={qprReportsCount}
            currentDataCount={qprReports.length}
            page={page}
            pageSize={pageSize}
            filters={
              <PageToolbar.Filters
                configs={[
                  {
                    label: "QPR Type",
                    options: qprTypeOptions,
                    selectedValues: qprType ? [qprType] : [],
                    onChange: (values) => this.handleFilter("qprType", values[0]),
                    width: 180,
                    mode: "single",
                  },
                  {
                    label: "Product",
                    options: modifiedProducts,
                    selectedValues: productId ? [productId] : [],
                    onChange: (values) => this.handleFilter("productId", values[0]),
                    onSearch: this.handleProductChange,
                    width: 220,
                    mode: "single",
                  },
                  {
                    label: "Company",
                    options: modifiedCompanies,
                    selectedValues: companyId ? [companyId] : [],
                    onChange: (values) => this.handleFilter("companyId", values[0]),
                    onSearch: this.handleCompanyChange,
                    width: 220,
                    mode: "single",
                  },
                  {
                    label: "Quarter",
                    options: quarterOptions,
                    selectedValues: quarter ? [quarter] : [],
                    onChange: (values) => this.handleFilter("quarter", values[0]),
                    width: 150,
                    mode: "single",
                  },
                  {
                    label: "Year",
                    options: yearOptions,
                    selectedValues: year ? [year] : [],
                    onChange: (values) => this.handleFilter("year", values[0]),
                    width: 120,
                    mode: "single",
                  },
                ]}
              />
            }
          />
          <Table
            loading={isLoading}
            scroll={{ x: 500 }}
            columns={columns}
            dataSource={qprReports}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: qprReportsCount,
              onChange: this.handlePageChange,
            }}
          />
        </div>
      </Card>
    );
  }
}
